import { useQuery } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import { axInst } from "../../Components/Login/Service";
import { Loader } from "rsuite";
import { FaTruck } from "react-icons/fa";
import { setFilter } from "../../store/slices/FilterSlicePvin";
import { useMemo, useState, useEffect, useRef } from "react";

/* =========================
   API CALLS
========================= */

const PvinCustomerSummaryApi = async (selectedFilter, dealerFilter) => {
  const payload = { ...selectedFilter, ...dealerFilter };
  const res = await axInst.post("pvin/account-summary", payload);
  return res.data;
};

const PvinCustomerSummaryNotMappedApi = async (
  selectedFilter,
  dealerFilter,
) => {
  const payload = { ...selectedFilter, ...dealerFilter };
  const res = await axInst.post("pvin/not-mapped-account-summary", payload);
  return res.data;
};

/* =========================
   MAIN COMPONENT
========================= */

const CustomerSummary = ({ dealerFilterPayload }) => {
  const dispatch = useDispatch();
  const loadMoreRef = useRef(null);

  const selectedFilter = useSelector(
    (state) => state.filtersPvin.selectedFilter,
  );

  /* ===== API CALLS ===== */

  const { data, isFetching, isError } = useQuery({
    queryKey: ["pvinCustomerData", selectedFilter, dealerFilterPayload],
    queryFn: () => PvinCustomerSummaryApi(selectedFilter, dealerFilterPayload),
  });

  const {
    data: data2,
    isFetching: isFetching2,
    isError: isError2,
  } = useQuery({
    queryKey: ["pvinCustomerData2", selectedFilter, dealerFilterPayload],
    queryFn: () =>
      PvinCustomerSummaryNotMappedApi(selectedFilter, dealerFilterPayload),
  });

  /* ===== MERGE DATA ===== */

  const mergedData = useMemo(
    () => [...(data || []), ...(data2 || [])],
    [data, data2],
  );

  /* ===== INFINITE SCROLL STATE ===== */

  const LOAD_COUNT = 60;
  const [visibleCount, setVisibleCount] = useState(LOAD_COUNT);

  const visibleData = mergedData.slice(0, visibleCount);

  /* ===== INTERSECTION OBSERVER ===== */

  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + LOAD_COUNT, mergedData.length),
          );
        }
      },
      { threshold: 1 },
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [mergedData.length]);

  const handleCardClick = (arn) => {
    dispatch(setFilter({ Accnt_Arn: [arn] }));
  };

  /* ===== LOADING ===== */

  if (isFetching || isFetching2)
    return (
      <div className="grid place-items-center h-40">
        <Loader />
      </div>
    );

  if (isError || isError2) return <div>Error loading data</div>;

  /* ===== GRADIENTS ===== */

  const gradients = [
    "bg-gradient-to-br from-blue-300 to-blue-400",
    "bg-gradient-to-br from-emerald-300 to-teal-400",
    "bg-gradient-to-br from-sky-200 to-sky-400",
    "bg-gradient-to-br from-fuchsia-300 to-fuchsia-400",
    "bg-gradient-to-br from-indigo-300 to-violet-400",
    "bg-gradient-to-br from-slate-300 to-slate-400",
  ];

  return (
    <div className="w-full shadow-lg rounded-md p-3">
      {/* ===== CARDS ===== */}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visibleData.map((item, index) => {
          const gradient = gradients[index % gradients.length];
          const isClickable = !!item.Par_Accnt_Arn;

          return (
            <div
              key={index}
              onClick={() => isClickable && handleCardClick(item.Par_Accnt_Arn)}
              title={
                !isClickable
                  ? "Key Account and ARN Mapping not available in Source System"
                  : ""
              }
              className={`
        rounded-2xl px-3 py-2 shadow-md text-white
        transition-all duration-300
        ${gradient}
        ${
          isClickable
            ? "cursor-pointer hover:scale-[1.03]"
            : "cursor-not-allowed opacity-70"
        }
      `}
            >
              {/* HEADER */}
              <div className="flex justify-between mb-2">
                {item.Account_Name && (
                  <p className="text-lg font-semibold line-clamp-2">
                    {item.Account_Name}
                  </p>
                )}

                {item.Last_purchase_model && (
                  <div className="flex flex-col items-center">
                    <FaTruck className="text-white text-xl" />
                    <span className="text-[11px] mt-1">
                      {item.Last_purchase_model}
                    </span>
                  </div>
                )}
              </div>

              {/* ARN */}
              {item.Par_Accnt_Arn && (
                <div className="text-sm font-semibold mb-2">
                  {item.Par_Accnt_Arn}
                </div>
              )}

              {/* DETAILS */}
              <div className="p-2 rounded-xl bg-white/20 backdrop-blur-sm text-sm space-y-1">
                {item.KA_code && (
                  <p>
                    <span className="font-medium">KA Code: </span>
                    <span className="font-semibold">{item.KA_code}</span>
                  </p>
                )}

                {item.KA_name && (
                  <p>
                    <span className="font-medium">KA Name: </span>
                    <span className="font-semibold">{item.KA_name}</span>
                  </p>
                )}

                {item.last_Purchase_date && (
                  <p>
                    <span className="font-medium">Last Purchase: </span>
                    <span className="font-semibold">
                      {item.last_Purchase_date}
                    </span>
                  </p>
                )}

                {item.fleet_size && (
                  <p>
                    <span className="font-medium">Fleet Size: </span>
                    <span className="font-semibold">{item.fleet_size}</span>
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== LOAD MORE TRIGGER ===== */}

      <div ref={loadMoreRef} className="h-10 flex justify-center mt-4">
        {visibleCount < mergedData.length && (
          <Loader size="sm" content="Loading more..." />
        )}
      </div>
    </div>
  );
};

export default CustomerSummary;
